<template>
<div class="admin-otp">
	<div class="container">
		<div class="page-header flex-center">
			<h1 class="page-title">
				One Time Password Page
			</h1>
		</div>

		<div class="page-content">
			<div class="d-flex justify-content-between">
				<div>
					<multiselect
						v-model="type"
						:options="optionType"
						track-by="key"
						label="label"
						:show-labels="false"
						:allow-empty="false"
						:multiple="false"
					/>
				</div>
				<div class="saerch-section">
					<div class="input-group mb-3">
						<div class="input-group-prepend">
							<button
								class="btn search-by-btn btn-outline-secondary dropdown-toggle"
								type="button"
								data-toggle="dropdown"
								aria-haspopup="true"
								aria-expanded="false"
							>
								{{ labelSearchBy }}
							</button>
							<div class="dropdown-menu">
								<a
									v-for="(option, i) in optionSearch"
									:key="i"
									class="dropdown-item"
									@click="selectSearchBy(option)"
								>
									{{ option.label }}
								</a>
							</div>
						</div>
						<input
							v-model="keyword"
							type="text"
							class="form-control input-radius"
							placeholder="Search"
							aria-label="Text input with dropdown button"
							:disabled="labelSearchBy === 'Search By'"
							@input="onSearch(keyword)"
						>
					</div>
				</div>
			</div>
			<div class="table-responsive">
				<OtpTable :is-loading="isLoading" :otps="otps" />
			</div>
		</div>
	</div>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import OtpTable from '@/components/otp/Table.vue';
import { getDataOtp } from '@/api/admin';
import { delay, duplicateVar } from '@/lib/helper';
import { OTP_TYPE } from '@/lib/statics';

const utcPlugin = require('dayjs/plugin/utc');
const timezonePlugin = require('dayjs/plugin/timezone');

// Extend dayjs with plugins
dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

export default {
	name: 'AdminOTP',
	data() {
		return {
			otps: [],
			isLoading: false,
			searchBy: null,
			keyword: null,
			type: null,
			labelSearchBy: 'Search By',
			isAll: false,
			optionType: duplicateVar(OTP_TYPE),
			selectType: null,
			page: 1,
			optionSearch: [
				{
					key: 'email',
					label: 'Email',
				},
				{
					key: 'user_id',
					label: 'UserId',
				},
			],
		};
	},
	components: {
		OtpTable,
	},
	computed: {
		...mapGetters({
			user: 'user',
		}),
		fetchParams() {
			const params = {
				page: this.page,
				limit: 10,
			};
			if (this.searchBy && this.keyword) {
				params.search_by = this.searchBy;
				params.keyword = this.keyword;
			}

			if (this.type) {
				params.type = this.type?.key;
			}
			return params;
		},
	},
	watch: {
		type(type) {
			if (type) {
				this.getAllData(true);
			}
		},
	},
	created() {
		this.getAllData();
		window.addEventListener('scroll', this.handleScroll);
	},
	methods: {
		getAllData(reset = true) {
			if (reset) {
				this.otps = [];
				this.page = 1;
			}
			this.isLoading = true;
			const params = this.fetchParams;
			const callback = (res) => {
				this.isLoading = false;
				const data = res?.data.map((otp) => {
					const expiredAt = this.setFormatTime(otp?.expired_at);

					const differenceInSeconds = this.setDiffSeconds(expiredAt);
					return {
						userId: otp?.user_id ?? '',
						email: otp?.email ?? '',
						temp_phone: otp?.temp_phone,
						type: otp?.type,
						code: otp?.code ?? '',
						created_at: otp?.created_at,
						remaining_at: differenceInSeconds,
					};
				});

				if (data.filter(curr => curr?.remaining_at > 0).length > 0) {
					this.otps = this.otps.concat(data.filter(curr => curr?.remaining_at > 0));
					this.page++;
				} else {
					this.isAll = true;
				}
				this.startCountdown(this.otps);
			};
			const err = () => {
				this.isLoading = false;
			};
			getDataOtp(params, callback, err);
		},
		startCountdown(otps) {
			otps.forEach((otp, index) => {
				otp.remaining_at = Number(otp.remaining_at);
				const inteval = setInterval(() => {
					if (otp.remaining_at > 0) {
						otp.remaining_at -= 1;
					} else if (otps?.[index]?.remaining_at === 0 ?? false) {
						clearInterval(inteval);
						otp.countdownInterval = null;
						this.otps.splice(index, 1);
					}
				}, 1000);
			});
		},
		setFormatTime(date, format = 'YYYY-MM-DD : HH:mm:ss') {
			return dayjs.utc(date).tz(this.user?.timezone).format(format);
		},
		setDiffSeconds(expiredAt) {
			const now = dayjs();
			if (expiredAt > now) {
				return 0;
			}
			return now?.diff(expiredAt, 'second') < 0 ? now?.diff(expiredAt, 'second') * -1 : 0;
		},
		onSearch(keyword) {
			delay(() => {
				this.keyword = keyword;
				this.getAllData();
			}, 500);
		},
		selectSearchBy(option) {
			this.searchBy = option.key;
			this.labelSearchBy = option?.label ?? 'Search By';
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isLoading && !this.isAll) {
					this.getAllData(false);
				}
			}
		},
	},
	beforeDestroy() {
		// Clear intervals to avoid memory leaks
		this.otps.forEach((otp) => {
			if (otp.countdownInterval) {
				clearInterval(otp.countdownInterval);
			}
		});
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style lang="scss" scoped>
.search-by-btn {
	border-color: #D9DEE2 !important;
}

.saerch-section {
	width: 40%;
}
</style>
